import { MealImageSize, MealImageStatus } from '@/types/mealPlan'

// The rendering surfaces a meal image can appear on. Each maps to exactly one
// rendition size — history/list rows load the small square, cards the
// mid square, hero the large square. A closed set, so it's an enum callers
// select from rather than bare strings.
export enum MealImageSurface {
  ListRow = 'listRow',
  Card = 'card',
  Hero = 'hero'
}

const SURFACE_SIZE: Record<MealImageSurface, MealImageSize> = {
  [MealImageSurface.ListRow]: MealImageSize.Thumb,
  [MealImageSurface.Card]: MealImageSize.Card,
  [MealImageSurface.Hero]: MealImageSize.Hero
}

// The real rendition map for a meal, but ONLY once the pipeline has finished
// (imageStatus === Ready). Any earlier/failed state returns undefined so callers
// fall back to the placeholder. This is the single place the persistence
// `imageStatus` enum is read on the read path — view-models expose the resolved
// `renditions` instead of leaking the raw status field.
export function readyRenditions(meal: {
  images?: Partial<Record<MealImageSize, string>>
  imageStatus?: MealImageStatus
}): Partial<Record<MealImageSize, string>> | undefined {
  return meal.imageStatus === MealImageStatus.Ready ? meal.images : undefined
}

// Presentation-shaped image lifecycle derived from raw `imageStatus`: `ready` once renditions exist,
// `placeholder` only for terminal `Failed`, `loading` otherwise — including ABSENT (queued, not started).
export type MealImagePresentation = 'ready' | 'loading' | 'placeholder'

export function imageStateOf(meal: { imageStatus?: MealImageStatus }): MealImagePresentation {
  switch (meal.imageStatus) {
    case MealImageStatus.Ready:
      return 'ready'
    case MealImageStatus.Failed:
      return 'placeholder'
    default:
      // Generating, Pending, or not-yet-written — the photo is still on its way.
      return 'loading'
  }
}

// The two stand-in plates: centred, and deliberately offset off the right edge.
export enum MealPlaceholderArt {
  Centred = 'a2',
  Offset = 'a4'
}

const PLACEHOLDER_ARTS = [MealPlaceholderArt.Centred, MealPlaceholderArt.Offset]

// Stable per meal (FNV-1a over the id), so a list of pending photos varies but a meal never flips.
// A dinner with no meal behind it always gets the centred plate.
export function placeholderArtFor(mealId: string | null | undefined): MealPlaceholderArt {
  if (!mealId) return MealPlaceholderArt.Centred
  let hash = 0x811c9dc5
  for (let i = 0; i < mealId.length; i++) {
    hash = Math.imul(hash ^ mealId.charCodeAt(i), 0x01000193)
  }
  return PLACEHOLDER_ARTS[(hash >>> 0) % PLACEHOLDER_ARTS.length]
}

export interface PlaceholderContext {
  mealId: string | null | undefined
  isDark: boolean
}

// List rows are near-square thumbnails; cards and heroes are wide, where the square art loses its rim.
export function mealPlaceholderUrl(surface: MealImageSurface, { mealId, isDark }: PlaceholderContext): string {
  const shape = surface === MealImageSurface.ListRow ? 'square' : 'wide'
  return `/assets/meal-placeholders/${placeholderArtFor(mealId)}-${shape}-${isDark ? 'dark' : 'light'}.webp`
}

// Pick the right rendition URL for a surface, falling back to the meal's placeholder when
// the meal isn't ready (renditions undefined) or lacks that specific size. Pure
// sync — the stored values are already download URLs, so there are
// zero read-time getDownloadURL calls even when a list renders dozens of meals.
export function renditionUrl(
  renditions: Partial<Record<MealImageSize, string>> | undefined,
  surface: MealImageSurface,
  placeholder: PlaceholderContext
): string {
  return renditions?.[SURFACE_SIZE[surface]] ?? mealPlaceholderUrl(surface, placeholder)
}

// What MealPhoto needs: the surface's URL, plus the placeholder art to swap in if that URL fails to load.
export interface MealPhotoSource {
  src: string
  fallbackSrc: string
}

export function mealPhotoSource(
  renditions: Partial<Record<MealImageSize, string>> | undefined,
  surface: MealImageSurface,
  placeholder: PlaceholderContext
): MealPhotoSource {
  return { src: renditionUrl(renditions, surface, placeholder), fallbackSrc: mealPlaceholderUrl(surface, placeholder) }
}
